import { useRef, useState } from 'react'
import { IoChevronDown } from 'react-icons/io5'
import TemplateList from './TemplateList'
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layouts/PageLayout';
import CustomNavbar from '../../components/common/CustomNavbar';
import { whtsappIcon } from '../../asset';
import ECSLoader from '../../components/common/EcsLoader';
import { useOutsideClick } from '../../util/hooks/useDebounce';

const TABS = ['All', 'Approved', 'Pending', 'Draft', 'Rejected']

const TYPE_OPTIONS = ['All', 'Text', 'Image', 'Video', 'Document', 'Carousel']

export default function Template() {
    const navigate = useNavigate();

    const dropdownRef = useRef<HTMLDivElement | null>(null);
    useOutsideClick(dropdownRef, () => setOpen(false));

    const [open, setOpen] = useState(false)
    const [activeTab, setActiveTab] = useState('All')
    const [selectedTypes, setSelectedTypes] = useState<string[]>(['All'])
    const [isLoading, setIsLoading] = useState(false);

    const handleTypeToggle = (item: string) => {
        if (item === 'All') {
            setSelectedTypes(['All'])
            return
        }

        setSelectedTypes(prev => {
            const withoutAll = prev.filter(t => t !== 'All')
            if (withoutAll.includes(item)) {
                const updated = withoutAll.filter(t => t !== item)
                return updated.length === 0 ? ['All'] : updated
            } else {
                return [...withoutAll, item]
            }
        })
    }

    return (
        <PageLayout customNavbar={
            <CustomNavbar>
                <div className="flex gap-3 items-center w-max">
                    <img src={whtsappIcon} className="w-10 h-10" />
                    <span className="font-bold text-lg uppercase">Whatsapp Templates</span>
                </div>
            </CustomNavbar>
        }>
            <div className="rounded-4xl w-full bg-gray-100">
                <div className="flex flex-col rounded-4xl w-full p-4 bg-white min-h-screen relative overflow-hidden">
                    <ECSLoader show={isLoading} />


                    {/* Filters Row */}
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">

                        {/* Tabs */}
                        <div className="flex gap-7 overflow-x-auto no-scrollbar">
                            {TABS.map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`
                                        relative pb-2 whitespace-nowrap
                                        ${activeTab === tab
                                            ? 'text-brand-main font-bold'
                                            : 'text-text-secondary font-semibold'}
                                    `}
                                >
                                    {tab}
                                    {activeTab === tab && (
                                        <span className="absolute left-0 bottom-0 h-[2px] w-full bg-brand-main rounded-full" />
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Right actions */}
                        <div className="flex flex-col sm:flex-row gap-4 sm:items-center">

                            {/* Filter Button + Dropdown */}
                            <div ref={dropdownRef} className="relative w-full">
                                <div className="p-[2px] rounded-4xl bg-brand-blue-gradient w-full">
                                    <button
                                        onClick={() => setOpen(prev => !prev)}
                                        className="flex w-full items-center gap-2 px-5 py-2 rounded-4xl bg-white font-semibold text-text-secondary"
                                    >
                                        {selectedTypes.includes('All')
                                            ? 'Filter by Type'
                                            : selectedTypes.join(', ')}
                                        <IoChevronDown
                                            className={`ml-auto text-[16px] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                                        />
                                    </button>
                                </div>

                                {open && (
                                    <div className="absolute mt-2 right-0 w-[160px] rounded-xl border border-[#E0E0E0] bg-white p-3 shadow-md z-20">
                                        <div className="flex flex-col gap-2">
                                            {TYPE_OPTIONS.map(item => {
                                                const isChecked = selectedTypes.includes(item)
                                                return (
                                                    <label
                                                        key={item}
                                                        className="flex items-center gap-2 cursor-pointer"
                                                        onClick={() => handleTypeToggle(item)}
                                                    >
                                                        <div
                                                            className={`w-4 h-4 rounded border flex items-center justify-center shrink-0
                                                                ${isChecked
                                                                    ? 'bg-brand-main border-brand-main'
                                                                    : 'border-text-secondary'}`}
                                                        >
                                                            {isChecked && (
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="9" height="7" viewBox="0 0 9 7" fill="none">
                                                                    <path d="M7.66667 1L3.08333 5.58333L1 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        <span className={`text-sm font-semibold ${isChecked ? 'text-brand-main' : 'text-text-secondary'}`}>
                                                            {item}
                                                        </span>
                                                    </label>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Create Button */}
                            <button
                                onClick={() => navigate("/admin/templates/add")}
                                className="flex items-center justify-center px-5 py-3 rounded-4xl bg-brand-blue-gradient text-white font-bold whitespace-nowrap"
                            >
                                + Create New Template
                            </button>
                        </div>
                    </div>

                    <div className='flex flex-wrap gap-x-5 gap-y-5 sm:mt-10 mt-5'>

                        {/* Create New Template */}
                        <div className="flex p-7 justify-center items-center w-full sm:max-w-[280px] border-2 border-dashed border-gray-300 px-8 rounded-tl-4xl rounded-br-4xl rounded-2xl
                            hover:border-brand-main transition-all duration-200 cursor-pointer">
                            <div className="flex flex-col items-center text-center gap-4"
                                onClick={() => navigate("/admin/templates/add")}>
                                <div className="w-10 h-10 rounded-full border-2 border-text-secondary flex items-center justify-center text-text-secondary text-2xl font-bold leading-none">
                                    +
                                </div>
                                <p className="font-bold text-lg text-text-secondary">
                                    Create your next template
                                </p>
                                <p className="text-xs text-text-secondary max-w-[200px]">
                                    Start by creating a WhatsApp message template to connect with your customers instantly.
                                </p>
                            </div>
                        </div>

                        <TemplateList activeTab={activeTab} selectedTypes={selectedTypes} onLoadingChange={setIsLoading} page={0} pageSize={25} />
                    </div>
                </div>
            </div>
        </PageLayout>
    )
}
